import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { MrpLocationChip } from "@/components/mrp/MrpLocationChip";
import { PurchaseOrderHubLinkWithMegaventory } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { MrpSkuCatalogHoverHintListBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { HubPageTitle } from "@/components/HubPageTitle";
import { apiFetch } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { cn } from "@/lib/utils";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input, Label } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import { HoverHintContent, HoverHintWithGlyph } from "@/components/ui/title-help";
import {
  mrpPurchaseOrderListCacheKey,
  type HubPurchaseOrderStatus,
  type MrpPurchaseOrder,
  type MrpSkuHoverHintFields,
  type PurchaseOrderFulfillmentStatus,
  formatNumber,
  mrpPurchaseOrderIsHubOwned,
  purchaseOrderListDateCreated,
  mrpPurchaseOrderReceivingStatusLabel,
  MRP_PURCHASE_ORDER_RECEIVING_STATUS_HOVER_HINT,
  MRP_PURCHASE_ORDER_HUB_LIFECYCLE_HOVER_HINT,
  MRP_PURCHASE_ORDER_FULFILLMENT_HOVER_HINT,
  hubPurchaseOrderStatusBadgeTone,
  hubPurchaseOrderStatusLabel,
  normalizeHubPurchaseOrderStatus,
  purchaseOrderFulfillmentStatusBadgeTone,
  purchaseOrderFulfillmentStatusLabel,
} from "@/lib/mrp";
import { useTransferEndpointLocations } from "@/lib/transferEndpointLocations";
import {
  locationFilterQueryParam,
  operationalLocationFilterOptions,
} from "@/lib/operationalLocationFilter";
import { createHubPurchaseOrderAndNavigate } from "./purchaseOrderHubDraftNav";

/** Blur received / remaining / status while shell rows wait for full metrics. */
const METRICS_PENDING_CELL_CLASS =
  "blur-[2px] opacity-45 saturate-50 transition-[filter,opacity] duration-200 select-none";

type StatusFilter = "all" | "open" | "partial" | "received" | "cancelled";
type HubLifecycleFilter = "all" | HubPurchaseOrderStatus;
type FulfillmentFilter = "all" | PurchaseOrderFulfillmentStatus;

const HUB_LIFECYCLE_FILTER_OPTIONS: { value: HubLifecycleFilter; label: string }[] = [
  { value: "all", label: "All lifecycle" },
  { value: "draft", label: "Draft" },
  { value: "ordered", label: "Ordered" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

const FULFILLMENT_FILTER_OPTIONS: { value: FulfillmentFilter; label: string }[] = [
  { value: "all", label: "All fulfillment" },
  { value: "not_received", label: "Not received" },
  { value: "partially_received", label: "Partially received" },
  { value: "received", label: "Received" },
  { value: "partially_invoiced", label: "Partially invoiced" },
  { value: "invoiced", label: "Invoiced" },
];

type PurchaseOrderSortKey =
  | "po_number"
  | "mv_created_at"
  | "supplier"
  | "line_count"
  | "location_code"
  | "quantity_ordered"
  | "quantity_received"
  | "remaining_quantity"
  | "next_expected_arrival"
  | "status";

function comparePurchaseOrderRows(
  left: MrpPurchaseOrder,
  right: MrpPurchaseOrder,
  key: PurchaseOrderSortKey,
  direction: "asc" | "desc",
): number {
  const factor = direction === "asc" ? 1 : -1;

  if (key === "next_expected_arrival" || key === "mv_created_at") {
    const readDate = (row: MrpPurchaseOrder): string | null =>
      key === "mv_created_at" ? row.mv_created_at || null : row.next_expected_arrival;
    const leftDate = readDate(left);
    const rightDate = readDate(right);
    const leftEmpty = leftDate == null || leftDate.trim() === "";
    const rightEmpty = rightDate == null || rightDate.trim() === "";
    if (leftEmpty && rightEmpty) return 0;
    if (leftEmpty) return 1;
    if (rightEmpty) return -1;
    return leftDate.localeCompare(rightDate) * factor;
  }

  const readValue = (row: MrpPurchaseOrder): string | number => {
    switch (key) {
      case "po_number":
        return row.po_number?.toLowerCase() ?? "";
      case "supplier":
        return row.supplier?.toLowerCase() ?? "";
      case "line_count":
        return row.line_count;
      case "location_code":
        return row.location_code;
      case "quantity_ordered":
        return row.quantity_ordered;
      case "quantity_received":
        return row.quantity_received ?? Number.NEGATIVE_INFINITY;
      case "remaining_quantity":
        return row.remaining_quantity ?? Number.NEGATIVE_INFINITY;
      case "status":
        return row.status ?? "";
      default:
        return "";
    }
  };

  const leftValue = readValue(left);
  const rightValue = readValue(right);
  if (typeof leftValue === "number" && typeof rightValue === "number") {
    return (leftValue - rightValue) * factor;
  }
  return String(leftValue).localeCompare(String(rightValue), undefined, {
    numeric: true,
    sensitivity: "base",
  }) * factor;
}

export function PurchaseOrderList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const viewerUserId = session?.user.id?.trim() ?? "";
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [hubLifecycleFilter, setHubLifecycleFilter] = useState<HubLifecycleFilter>("all");
  const [fulfillmentFilter, setFulfillmentFilter] = useState<FulfillmentFilter>("all");
  const [locationFilter, setLocationFilter] = useState<Set<string>>(() => new Set());
  const [locationFilterSearch, setLocationFilterSearch] = useState("");
  const [search, setSearch] = useState("");
  const { locations: transferLocations, isLoading: locationsLoading } = useTransferEndpointLocations();
  const locationOptions = useMemo(
    () => operationalLocationFilterOptions(transferLocations),
    [transferLocations],
  );
  const availableLocationCodes = useMemo(
    () => locationOptions.map((option) => option.value),
    [locationOptions],
  );

  useEffect(() => {
    if (availableLocationCodes.length === 0) return;
    setLocationFilter((current) => {
      if (current.size === 0) return new Set(availableLocationCodes);
      return current;
    });
  }, [availableLocationCodes]);

  const locationQuery = useMemo(
    () => locationFilterQueryParam(locationFilter, availableLocationCodes),
    [locationFilter, availableLocationCodes],
  );

  const listQueryKey = useMemo(
    () =>
      ["mrp", "purchase-orders", { statusFilter, locations: locationQuery ?? "" }] as const,
    [statusFilter, locationQuery],
  );

  const listQueryParams = useMemo(
    () => ({
      status: statusFilter === "all" ? undefined : statusFilter,
      location: locationQuery,
    }),
    [statusFilter, locationQuery],
  );
  const [sortState, setSortState] = useState<{ column: PurchaseOrderSortKey; dir: "asc" | "desc" }>({
    column: "mv_created_at",
    dir: "desc",
  });

  const shellQuery = useQuery({
    queryKey: [...listQueryKey, "shell"],
    queryFn: () =>
      apiFetch<{ rows: MrpPurchaseOrder[]; metrics_complete: boolean }>("/api/mrp/purchase-orders", {
        query: { ...listQueryParams, view: "shell", background: "1" },
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const fullQuery = useQuery({
    queryKey: [...listQueryKey, "full"],
    queryFn: () =>
      apiFetch<{ rows: MrpPurchaseOrder[]; metrics_complete: boolean }>("/api/mrp/purchase-orders", {
        query: { ...listQueryParams, background: "1" },
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const purchaseOrderHubCacheChannels = useMemo(() => {
    const shared = {
      status: listQueryParams.status,
      location: listQueryParams.location,
      viewerUserId,
    };
    return [
      HUB_CACHE_CHANNEL.mrpPurchaseOrders(mrpPurchaseOrderListCacheKey({ ...shared, view: "shell" })),
      HUB_CACHE_CHANNEL.mrpPurchaseOrders(mrpPurchaseOrderListCacheKey({ ...shared, view: "full" })),
    ];
  }, [listQueryParams, viewerUserId]);

  useHubCacheStream({
    channels: purchaseOrderHubCacheChannels,
    enabled: true,
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
      }
    },
  });

  const ordersQuery = fullQuery.data ? fullQuery : shellQuery;
  const metricsPending = Boolean(shellQuery.isSuccess && !fullQuery.isSuccess);

  const orderCpnList = useMemo(() => {
    const rows = ordersQuery.data?.rows ?? [];
    const cpns = rows.flatMap((row) => row.cpns);
    return [...new Set(cpns.map((cpn) => cpn.trim()).filter(Boolean))].sort();
  }, [ordersQuery.data]);

  const skuHintsQuery = useQuery({
    queryKey: ["mrp", "sku-hover-hints", orderCpnList],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: orderCpnList.join(",") },
      }),
    enabled: orderCpnList.length > 0 && ordersQuery.isSuccess,
    refetchOnWindowFocus: false,
  });

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = ordersQuery.data?.rows ?? [];
    const matched = !needle
      ? rows
      : rows.filter((row) => {
          if (row.po_number?.toLowerCase().includes(needle)) return true;
          if (row.supplier?.toLowerCase().includes(needle)) return true;
          if (row.cpns.some((cpn) => cpn.toLowerCase().includes(needle))) return true;
          return false;
        });
    const hubFiltered = matched.filter((row) => {
      if (!mrpPurchaseOrderIsHubOwned(row)) {
        return hubLifecycleFilter === "all" && fulfillmentFilter === "all";
      }
      if (metricsPending && fulfillmentFilter !== "all") {
        return true;
      }
      const lifecycle = normalizeHubPurchaseOrderStatus(row.hub_status);
      if (hubLifecycleFilter !== "all" && lifecycle !== hubLifecycleFilter) {
        return false;
      }
      if (fulfillmentFilter !== "all" && row.fulfillment_status !== fulfillmentFilter) {
        return false;
      }
      return true;
    });
    const copy = [...hubFiltered];
    copy.sort((left, right) => comparePurchaseOrderRows(left, right, sortState.column, sortState.dir));
    return copy;
  }, [
    ordersQuery.data,
    search,
    sortState.column,
    sortState.dir,
    hubLifecycleFilter,
    fulfillmentFilter,
    metricsPending,
  ]);

  const onSortHeader = (column: PurchaseOrderSortKey): void => {
    setSortState((previous) => {
      if (previous.column === column) {
        return { column, dir: previous.dir === "asc" ? "desc" : "asc" };
      }
      const descendingDefaultColumns: PurchaseOrderSortKey[] = [
        "line_count",
        "quantity_ordered",
        "quantity_received",
        "remaining_quantity",
        "mv_created_at",
      ];
      return {
        column,
        dir: descendingDefaultColumns.includes(column) ? "desc" : "asc",
      };
    });
  };

  const skuHoverHints = skuHintsQuery.data?.sku_hover_hints ?? {};

  const purchaseOrderColumns = useMemo((): HubDataTableColumn<MrpPurchaseOrder, PurchaseOrderSortKey>[] => {
    return [
      {
        id: "po_number",
        header: "PO",
        sortKey: "po_number",
        overflow: "nowrap",
        sortValue: (row) => row.po_number?.toLowerCase() ?? "",
        cell: (row) => (
          <span className="font-mono text-xs">
            <PurchaseOrderHubLinkWithMegaventory
              purchaseOrderId={row.id}
              poNumber={row.po_number}
              purchaseOrderMvId={row.purchase_order_mv_id}
              systemOfRecord={row.system_of_record}
            />
          </span>
        ),
      },
      {
        id: "mv_created_at",
        header: "Date created",
        sortKey: "mv_created_at",
        overflow: "nowrap",
        sortValue: (row) => row.mv_created_at ?? "",
        cell: (row) => <span className="text-xs">{purchaseOrderListDateCreated(row)}</span>,
      },
      {
        id: "supplier",
        header: "Supplier",
        sortKey: "supplier",
        sortValue: (row) => row.supplier?.toLowerCase() ?? "",
        cell: (row) => <span className="text-xs">{row.supplier ?? "—"}</span>,
      },
      {
        id: "line_count",
        header: "Lines",
        sortKey: "line_count",
        sortValue: (row) => row.line_count,
        cell: (row) => (
          <HoverHintContent
            hoverHint={
              <MrpSkuCatalogHoverHintListBody rows={row.line_rows} hints={skuHoverHints} />
            }
            block
          >
            <span className="text-xs">
              {row.line_count} {row.line_count === 1 ? "line" : "lines"}
              {row.cpns.length > 0 ? (
                <span className="mt-0.5 block max-w-[14rem] truncate font-mono text-[11px] text-slate-500">
                  {row.cpns.slice(0, 3).join(", ")}
                  {row.cpns.length > 3 ? ` +${row.cpns.length - 3}` : ""}
                </span>
              ) : null}
            </span>
          </HoverHintContent>
        ),
      },
      {
        id: "location_code",
        header: "Location",
        sortKey: "location_code",
        sortValue: (row) => row.location_code,
        cell: (row) => (
          <span className="text-xs">
            <MrpLocationChip code={row.location_code} />
          </span>
        ),
      },
      {
        id: "quantity_ordered",
        header: "Ordered",
        sortKey: "quantity_ordered",
        align: "right",
        sortValue: (row) => row.quantity_ordered,
        cell: (row) => (
          <span className="font-mono text-xs">{formatNumber(row.quantity_ordered)}</span>
        ),
      },
      {
        id: "quantity_received",
        header: "Received",
        sortKey: "quantity_received",
        align: "right",
        sortValue: (row) => row.quantity_received ?? Number.NEGATIVE_INFINITY,
        cell: (row) => (
          <span className={cn("font-mono text-xs", metricsPending && METRICS_PENDING_CELL_CLASS)}>
            {formatNumber(row.quantity_received ?? 0)}
          </span>
        ),
      },
      {
        id: "remaining_quantity",
        header: "Remaining",
        sortKey: "remaining_quantity",
        align: "right",
        sortValue: (row) => row.remaining_quantity ?? Number.NEGATIVE_INFINITY,
        cell: (row) => (
          <span
            className={cn(
              "font-mono text-xs text-slate-500",
              metricsPending && METRICS_PENDING_CELL_CLASS,
            )}
          >
            {formatNumber(row.remaining_quantity ?? 0)}
          </span>
        ),
      },
      {
        id: "next_expected_arrival",
        header: "Next expected",
        sortKey: "next_expected_arrival",
        sortValue: (row) => row.next_expected_arrival ?? "",
        cell: (row) => <span className="text-xs">{row.next_expected_arrival ?? "—"}</span>,
      },
      {
        id: "status",
        header: (
          <span className="inline-flex items-center gap-0.5">
            Status
            <HoverHintWithGlyph
              hoverHint={`Hub-owned rows show lifecycle and fulfillment. Megaventory rows show ${MRP_PURCHASE_ORDER_RECEIVING_STATUS_HOVER_HINT}`}
            />
          </span>
        ),
        sortKey: "status",
        sortValue: (row) => row.status ?? "",
        cell: (row) => (
          <span className="text-xs">
            {mrpPurchaseOrderIsHubOwned(row) ? (
              <div className="flex flex-col gap-1">
                <HoverHintContent hoverHint={MRP_PURCHASE_ORDER_HUB_LIFECYCLE_HOVER_HINT} block>
                  <Badge tone={hubPurchaseOrderStatusBadgeTone(normalizeHubPurchaseOrderStatus(row.hub_status))}>
                    {hubPurchaseOrderStatusLabel(normalizeHubPurchaseOrderStatus(row.hub_status))}
                  </Badge>
                </HoverHintContent>
                {row.fulfillment_status ? (
                  <HoverHintContent hoverHint={MRP_PURCHASE_ORDER_FULFILLMENT_HOVER_HINT} block>
                    <Badge tone={purchaseOrderFulfillmentStatusBadgeTone(row.fulfillment_status)}>
                      {purchaseOrderFulfillmentStatusLabel(row.fulfillment_status)}
                    </Badge>
                  </HoverHintContent>
                ) : metricsPending ? (
                  <span className={METRICS_PENDING_CELL_CLASS}>
                    <Badge tone="neutral">Fulfillment</Badge>
                  </span>
                ) : null}
              </div>
            ) : (
              <HoverHintContent hoverHint={MRP_PURCHASE_ORDER_RECEIVING_STATUS_HOVER_HINT} block>
                <span className={cn(metricsPending && METRICS_PENDING_CELL_CLASS)}>
                  {mrpPurchaseOrderReceivingStatusLabel(row.status ?? "open")}
                </span>
              </HoverHintContent>
            )}
          </span>
        ),
      },
    ];
  }, [metricsPending, skuHoverHints]);

  const filtersActive =
    statusFilter !== "all" ||
    hubLifecycleFilter !== "all" ||
    fulfillmentFilter !== "all" ||
    search.trim() !== "" ||
    locationFilter.size !== availableLocationCodes.length;

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <HubPageTitle>Purchase orders</HubPageTitle>
        <Button
          type="button"
          onClick={() => {
            void createHubPurchaseOrderAndNavigate(navigate, { location_code: "sgs" });
          }}
        >
          Create purchase order
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</span>
            {(["all", "open", "partial", "received", "cancelled"] as StatusFilter[]).map((status) => (
              <Button
                key={status}
                size="sm"
                variant={statusFilter === status ? "primary" : "secondary"}
                onClick={() => setStatusFilter(status)}
              >
                {status}
              </Button>
            ))}
          </div>
          <div className="mt-3 flex max-w-xl flex-col gap-2">
            <Label htmlFor="purchase-order-location-filter">Locations</Label>
            <SearchableMultiSelect
              id="purchase-order-location-filter"
              selectedValues={locationFilter}
              onSelectedValuesChange={setLocationFilter}
              options={locationOptions}
              searchValue={locationFilterSearch}
              onSearchChange={setLocationFilterSearch}
              placeholder="All locations"
              searchPlaceholder="Search locations…"
              allSelectedLabel="All locations"
              noneSelectedLabel="No locations selected"
              emptyMessage="No locations match."
              isLoading={locationsLoading}
              panelMinWidth={280}
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Hub lifecycle</span>
            {HUB_LIFECYCLE_FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant={hubLifecycleFilter === option.value ? "primary" : "secondary"}
                onClick={() => setHubLifecycleFilter(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Fulfillment</span>
            {FULFILLMENT_FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant={fulfillmentFilter === option.value ? "primary" : "secondary"}
                onClick={() => setFulfillmentFilter(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div className="mt-3">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by PO number, CPN, or supplier…"
            />
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={ordersQuery.isPending ? "Loading purchase orders…" : `${filteredRows.length} purchase orders`}
        columns={purchaseOrderColumns}
        rows={filteredRows}
        rowKey={(row) => row.id}
        sortKey={sortState.column}
        sortDir={sortState.dir}
        onSort={onSortHeader}
        loading={ordersQuery.isPending}
        loadingMessage="Loading purchase orders…"
        error={ordersQuery.isError ? ordersQuery.error.message : null}
        empty="No purchase orders."
        isFiltered={filtersActive}
        filteredEmpty="No purchase orders match this filter."
        tableClassName="min-w-full text-sm"
      />
    </div>
  );
}
